/**
 * Chamber of Trial: the trial spawner's enemy list and spawn positions.
 *
 * `_enemyIndex` and `_spawnPos` in the trial JSON (Title Data key
 * EC2_TRIAL_STAGE_3.0) are positional indices into two scene-bound Unity
 * lists — `EnemySpawner.enemyDatabase` and `EnemySpawner.allSpawnPositions`
 * on the ONE spawner that `TrialCommencer` in
 * `Assets/Scenes/Challenge_ChamberOfTrial/ChamberOfTrial.unity` points at
 * (hierarchy path `Spawner/Main`; the scene has 15 EnemySpawner instances
 * total, only this one matters). `src/data/trial-export.json` is a faithful
 * mirror of those two lists, produced by the
 * `TrialCommencer.ExportTrialTablesJson` Odin button in the Epic-Conquest-2
 * repo (`Assets/Prefabs/Mobile/Script/Trial/TrialCommencer.cs`).
 *
 * To regenerate: open ChamberOfTrial.unity, select the "Monolith" GameObject
 * (it holds the TrialCommencer component), expand the Helper foldout, press
 * "Export Trial Tables Json". Copy the resulting
 * `Assets/_TrialExport.json` over this file's twin, `trial-export.json`,
 * unmodified. Do not hand-edit the JSON — array order and `index` must both
 * come straight from the export, or a validator built on top of this module
 * would validate against a lie.
 *
 * `Monolith_Attack` / `TrialChallengeType` are enum-bound, not scene-bound —
 * they live in `trial-defense.ts` / `trial-defense.json` and regenerate by a
 * different route (the I2 localization asset, not the Editor). The two
 * modules stay separate on purpose; do not merge them.
 *
 * ## Empty slots are counter placeholders
 *
 * An entry can be *structurally* in range (`0 <= index <= enemyDatabase.length
 * - 1`) while still being an empty slot in the Unity list — no `_prefab`,
 * no `_asset`. `EnemySpawner.ProceedSpawn` (EnemySpawner.cs:466-482) resolves
 * neither branch for such an entry and spawns nothing. Bounds-checking alone
 * (`isValidEnemyIndex`) does not catch this — it is IN range.
 * `isEmptyEnemySlot` is the separate check for it.
 *
 * That is an authoring idiom, not a fault. A wave's kill budget is
 * `spawnData.Count - 1` (`EnemySpawner.SpawnWave`), and enemies spawned at
 * runtime by other enemies — Cave Mite's mites — report their deaths into the
 * same counter through `Main_Spawner.Dead()` without having a `spawnData` row
 * of their own. Padding with empty slots is how the budget accounts for them.
 * Live floor 14 is the worked example: 2x index 21 (Cave Mite (Big)) plus 12x
 * the empty slot = a budget of 14, for 2 parents plus 12 children.
 *
 * As of the export dated in this file's `generatedAt`, index 30 is such a
 * slot. The validator warns on it (the count still has to be right, and only
 * the author knows how many children a parent spawns) and never errors.
 */

import data from "@/data/trial-export.json";

export type EnemyClass = "Creep" | "Boss";

export interface TrialEnemy {
  index: number;
  /** Prefab name, or null for an empty enemyDatabase slot. Not localized. */
  prefab: string | null;
  /**
   * Localized display name, falling back to the prefab name, falling back
   * to "-" only when the slot is genuinely empty. Never an empty string.
   */
  name: string;
  class: EnemyClass;
}

export interface TrialSpawnPosition {
  index: number;
  /** Transform name in the scene. Null only if the transform itself is null. */
  name: string | null;
}

interface RawExport {
  generatedAt: string;
  scene: string;
  spawnerPath: string;
  enemies: TrialEnemy[];
  spawnPositions: TrialSpawnPosition[];
  spawnerEnemyCount: number;
  spawnPositionCount: number;
}

const raw = data as RawExport;

export const TRIAL_EXPORT_GENERATED_AT: string = raw.generatedAt;
export const TRIAL_EXPORT_SCENE: string = raw.scene;
export const TRIAL_EXPORT_SPAWNER_PATH: string = raw.spawnerPath;

export const TRIAL_ENEMIES: TrialEnemy[] = raw.enemies;
export const TRIAL_SPAWN_POSITIONS: TrialSpawnPosition[] = raw.spawnPositions;

if (
  typeof console !== "undefined" &&
  (raw.enemies.length !== raw.spawnerEnemyCount ||
    raw.spawnPositions.length !== raw.spawnPositionCount)
) {
  // eslint-disable-next-line no-console
  console.warn(
    "[trial-catalog] trial-export.json's array lengths do not match its own " +
      "spawnerEnemyCount/spawnPositionCount fields — the export may have been hand-edited.",
    {
      enemies: raw.enemies.length,
      spawnerEnemyCount: raw.spawnerEnemyCount,
      spawnPositions: raw.spawnPositions.length,
      spawnPositionCount: raw.spawnPositionCount,
    }
  );
}

/** Highest legal `_enemyIndex`. Structural bound only — see isEmptyEnemySlot. */
export const ENEMY_INDEX_MAX_VALUE = TRIAL_ENEMIES.length - 1;
/** Highest legal `_spawnPos`. `-1` is also legal (means "random position"). */
export const SPAWN_POS_MAX_VALUE = TRIAL_SPAWN_POSITIONS.length - 1;

const enemyByIndex = new Map(TRIAL_ENEMIES.map((e) => [e.index, e]));
const spawnPositionByIndex = new Map(TRIAL_SPAWN_POSITIONS.map((p) => [p.index, p]));

/** Structural bounds check only. An in-range index can still be an empty slot — see isEmptyEnemySlot. */
export function isValidEnemyIndex(index: number): boolean {
  return enemyByIndex.has(index);
}

/**
 * `_spawnPos: -1` is deliberate "random position" per EnemySpawner and is
 * legal, distinctly from an out-of-range positive index.
 */
export function isValidSpawnPos(pos: number): boolean {
  return pos === -1 || spawnPositionByIndex.has(pos);
}

/**
 * True when `index` is in range but the enemyDatabase slot is empty (no
 * prefab, no asset), so the game spawns nothing for it.
 *
 * This is a deliberate authoring idiom, NOT a broken entry. A wave's kill
 * budget is `spawnData.Count - 1` (EnemySpawner.SpawnWave) and the wave
 * clears when that counter goes negative. Enemies spawned at runtime by other
 * enemies report their deaths into the same counter via Main_Spawner.Dead()
 * but have no spawnData row, so empty slots are padded in to reserve budget
 * for them. Live floor 14 does exactly this: 2 Cave Mites + 12 placeholders.
 *
 * Treat it as a distinct, benign category — neither a valid enemy nor an
 * out-of-range index.
 */
export function isEmptyEnemySlot(index: number): boolean {
  const enemy = enemyByIndex.get(index);
  return enemy !== undefined && enemy.prefab === null;
}

export function enemyName(index: number): string {
  const found = enemyByIndex.get(index);
  if (!found) return `Unknown enemy (${index})`;
  // The export writes "-" for an empty slot, faithfully mirroring the scene.
  // Give it a name that says what it is for, so the dropdown is choosable
  // rather than mysterious.
  if (found.prefab === null) return `— Counter placeholder (spawns nothing)`;
  return found.name;
}

export function spawnPositionName(pos: number): string {
  if (pos === -1) return "Random position";
  return spawnPositionByIndex.get(pos)?.name ?? `Unknown position (${pos})`;
}

export function enemy(index: number): TrialEnemy | undefined {
  return enemyByIndex.get(index);
}

export function spawnPosition(pos: number): TrialSpawnPosition | undefined {
  return spawnPositionByIndex.get(pos);
}
